import { IInsightsContext, InsightsContext } from '@/context/InsightsContext';
import { columnsChanPop, columnsListPop, columnsUserGroup, columnsContentPopRankings } from '@/data/insightsColumns';
import { breakDownConvert, addRankings, buildCompareDownloadLink } from  '@/helpers/Insights';
import { IContentTable, initContentTable } from  '@/interfaces/IContentTable';
import { IApiSettings, IHttpRequest } from  '@/interfaces/IHttp';
import { Direction, FilterPage, IAggregated, IBreakdown, InitSearchFilter, ISearchFilter, OrderBy, Toggle } from  '@/interfaces/insightsp2';
import { container, TYPES } from "@/inversifyconfig/container.config";
import { useContext, useEffect, useState } from 'react';
import BreakdownCompare from './BreakdownCompare';
import ContentTable from './ContentTable';
import SearchFilter from './SearchFilter';
import DateLabelBuilder from './DateLabelBuilder';
import {tooltipData} from '../../../data/insights';
import ToolTipWrapper from '../Tooltip/ToolTipWrapper';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import GenError from './GenError';
import { removeTimeZone } from  '@/helpers/Dates';
import  '@/css/insightsContent.scss';

const Content = () => {

    // Context 
    const context:IInsightsContext = useContext(InsightsContext);

    // Search filter 
    const [searchFilterOptions, setSearchFilterOptions] = useState<ISearchFilter>(InitSearchFilter);

    // Aggregated 
    const [aggregatedDayOne, setAggregatedDayOne] = useState<IAggregated>(null);
    const [aggregatedDayTwo, setAggregatedDayTwo] = useState<IAggregated>(null);

    // Breakdown
    const [breakdownDayOne, setBreakdownDayOne] = useState<IBreakdown[]>(null);
    const [breakdownDayTwo, setBreakdownDayTwo] = useState<IBreakdown[]>(null);

    // Order by
    const [contentPopOrderBy, setContentPopOrderBy] = useState<OrderBy>({column:"views", direction:Direction.dsc});
    const [userGroupOrderBy, setUserGroupOrderBy] = useState<OrderBy>({column:"uniqueViews", direction:Direction.dsc});
    const [listPopOrderBy, setListPopOrderBy] = useState<OrderBy>({column:"views", direction:Direction.dsc});
    const [chanPopOrderBy, setChanPopOrderBy] = useState<OrderBy>({column:"views", direction:Direction.dsc});

    // Data day 1
    const [contentPop, setContentPop] = useState<IContentTable>(initContentTable);
    const [userGroup, setUserGroup] = useState<IContentTable>(initContentTable);
    const [listPop, setListPop] = useState<IContentTable>(initContentTable);
    const [chanPop, setChanPop] = useState<IContentTable>(initContentTable);

    // Tables toggle
    const [contentPopToggle, setContentPopToggle] = useState<Toggle>(Toggle.date1);
    const [userGroupToggle, setUserGroupToggle] = useState<Toggle>(Toggle.date1);
    const [twoColToggle, setTwoColToggle] = useState<Toggle>(Toggle.date1);

    // Loading
    const [initLoad, setInitLoad] = useState<boolean>(true);
    const [genError, setGenError] = useState<boolean>(false);

    // API
    const apiSettings = container.get<IApiSettings>(TYPES.IApiSettings);
 
    // Init
    useEffect(() => {

        context.loadTracker.setLoadMin(6);
        context.setFilterPage(FilterPage.content);

        search();
    }, []);

    // Gen error hide loading
    useEffect(() => {
        if (genError){
            context.setLoading(false);
        }
    }, [genError]);

    // Search updated
    useEffect(() => {

        // Dont run on mount
        if (initLoad) return

        search();     

    }, [searchFilterOptions]);

    // Content popularity table
    useEffect(() => {

        if(!initLoad){

            context.setLoading(true);

            orderByDoubleQuery('insights/content-popularity', contentPopOrderBy).then(({day1Data, day2Data}) => {

                // Add modal details to day 2
                day2Data = setDayTwoModal(day2Data);

                // Rankings
                const day1WithRankings = addRankings(day1Data, day2Data);
                
                // Set agg day 1
                setAggregatedDayOne(prevState => ({
                    ...prevState,
                    insightsContentPopularityResource: {data:day1WithRankings}
                }));

                // Set agg day 2              
                setAggregatedDayTwo(prevState => ({
                    ...prevState,
                    insightsContentPopularityResource: {data:day2Data}
                }));

                // Set current display
                const currentData = contentPopToggle === Toggle.date1 ? day1WithRankings : day2Data;

                setContentPop(prevState => ({
                    ...prevState,
                    data: currentData
                }));

                // loading
                context.setLoading(false);
            })
        }

    },[contentPopOrderBy]);

    useEffect(() => {

        if(!initLoad){
            const data = contentPopToggle === Toggle.date1 ? aggregatedDayOne.insightsContentPopularityResource.data : aggregatedDayTwo.insightsContentPopularityResource.data;
            contentPopToggleDataSet(data);
        }

    }, [contentPopToggle]);

    // User group activity
    useEffect(() => {
        
        if(!initLoad){

            context.setLoading(true);

            orderByDoubleQuery('insights/user-group-activity', userGroupOrderBy).then(({day1Data, day2Data}) => {
         
                // Set agg day 1
                setAggregatedDayOne(prevState => ({
                    ...prevState,
                    insightsUserGroupActivityResource: {data:day1Data}
                }));

                // Set agg day 2              
                setAggregatedDayTwo(prevState => ({
                    ...prevState,
                    insightsUserGroupActivityResource: {data:day2Data}
                }));
 
                // Set current display
                const currentData = userGroupToggle === Toggle.date1 ? day1Data : day2Data;

                setUserGroup(prevState => ({
                    ...prevState,
                    data: currentData
                }));

                // loading
                context.setLoading(false);
               
            })
        }

    }, [userGroupOrderBy]);

    useEffect(() => {

        if(!initLoad){

            const dataUserGroup = userGroupToggle === Toggle.date1 ? aggregatedDayOne.insightsUserGroupActivityResource.data : aggregatedDayTwo.insightsUserGroupActivityResource.data;

            userGroupToggleDataSet(dataUserGroup);

        }

    }, [userGroupToggle]);

    // Two col toggle - switches both list & channel
    useEffect(() => {

        if(!initLoad){

            // List popularity
            const dataListPop = twoColToggle === Toggle.date1 ? aggregatedDayOne.insightsListPopularityResource.data : aggregatedDayTwo.insightsListPopularityResource.data;

            listPopToggleDataSet(dataListPop);

            // Channel popularity
            const dataChanPop = twoColToggle === Toggle.date1 ? aggregatedDayOne.insightsChannelPopularityResource.data : aggregatedDayTwo.insightsChannelPopularityResource.data;

            chanPopDataSet(dataChanPop);
        }

    }, [twoColToggle]);
    
    // List popularity
    useEffect(() => {

        if(!initLoad){

            context.setLoading(true);

            orderByDoubleQuery('insights/list-popularity', listPopOrderBy).then(({day1Data, day2Data}) => {

                // Set agg day 1
                setAggregatedDayOne(prevState => ({
                    ...prevState,
                    insightsListPopularityResource: {data:day1Data}
                }));

                // Set agg day 2              
                setAggregatedDayTwo(prevState => ({
                    ...prevState,
                    insightsListPopularityResource: {data:day2Data}
                }));

                // Set current display
                const currentData = twoColToggle === Toggle.date1 ? day1Data : day2Data;

                setListPop(prevState => ({
                    ...prevState,
                    data: currentData
                }));

                // loading
                context.setLoading(false);
            })
        }   

    }, [listPopOrderBy]);

    // Channel popularity
    useEffect(() => {

        if(!initLoad){

            context.setLoading(true);

            orderByDoubleQuery('insights/channel-popularity', chanPopOrderBy).then(({day1Data, day2Data}) => {

                // Set agg day 1
                setAggregatedDayOne(prevState => ({
                    ...prevState,
                    insightsChannelPopularityResource: {data:day1Data}
                }));

                // Set agg day 2              
                setAggregatedDayTwo(prevState => ({
                    ...prevState,
                    insightsChannelPopularityResource: {data:day2Data}
                }));

                // Set current display
                const currentData = twoColToggle === Toggle.date1 ? day1Data : day2Data;

                setChanPop(prevState => ({
                    ...prevState,
                    data: currentData
                }));

                // loading
                context.setLoading(false);
            })
        }  

    }, [chanPopOrderBy]);
 
    function search():void {

        let day1Data, day2Data, contentPopDay2;

        // Loading
        context.loadTracker.dispatch({type: 'reset'})
        context.setLoading(true);

        // Aggregated day 1
        const agg = container.get<IHttpRequest>(TYPES.IHttpRequest).get(`insights/aggregated?${urlBuilder(Toggle.date1)}`).then((x) => {

            // Store global
            day1Data = x.data;

            // User groups
            setUserGroup({
                title:'User Group Activity',
                data:x.data.insightsUserGroupActivityResource.data,
                columns:columnsUserGroup(),
                csvDownload: `${apiSettings.apiBaseUrl()}insights/user-group-activity-csv?${urlBuilder(Toggle.date1)}`
            });

            // List pop
            setListPop({
                title:'List Popularity',
                tooltip: tooltipData.listPop,
                data:x.data.insightsListPopularityResource.data,
                columns:columnsListPop(),
                csvDownload: `${apiSettings.apiBaseUrl()}insights/list-popularity-csv?${urlBuilder(Toggle.date1)}`
            });

            // Chan pop
            setChanPop({
                title:'Workspace Popularity',
                tooltip: tooltipData.channelAct,
                data:x.data.insightsChannelPopularityResource.data,
                columns:columnsChanPop(),
                csvDownload: `${apiSettings.apiBaseUrl()}insights/channel-popularity-csv?${urlBuilder(Toggle.date1)}`
            });

            // Breakdown
            setBreakdownDayOne(breakDownConvert(x.data.insightsWorkspaceInsightsResource)); 

        }).catch((error) => {
            console.log(`Aggregated error day 1: ${error}`)
            setGenError(true)
        })

        // Aggregated day 2
        const agg2 = container.get<IHttpRequest>(TYPES.IHttpRequest).get(`insights/aggregated?${urlBuilder(Toggle.date2)}`).then((x) => {

            if (x.data && x.data.insightsContentPopularityResource.data){
                x.data.insightsContentPopularityResource.data = setDayTwoModal(x.data.insightsContentPopularityResource.data); 
            }
                        
            // Save for move
            day2Data = x.data;
            
            // Content popularity - save for later
            contentPopDay2 = x.data.insightsContentPopularityResource.data;

            // Breakdown day 2
            setBreakdownDayTwo(breakDownConvert(x.data.insightsWorkspaceInsightsResource));

            // Store data
            setAggregatedDayTwo(x.data);

        }).catch((error) => {
            console.log(`Aggregated error day 2: ${error}`)
        })

        // Finish loading
        Promise.all([agg, agg2]).then(() => {

            setContentPopOnSearch(day1Data, contentPopDay2);

            // Move data if searching with second toggle selected
            moveDataOnSecondToggle(day2Data);

            setInitLoad(false);


        }).finally(() => {
            context.setLoading(false);
        })
    }

    async function orderByDoubleQuery(baseurl:string, orderBy:OrderBy){

        let day1Data, day2Data;

        // Day 1
        await container.get<IHttpRequest>(TYPES.IHttpRequest).get(`${baseurl}?${urlBuilder(Toggle.date1)}&top=100${orderByBuilder(orderBy)}`).then((x) => {

            day1Data = x.data.data;  

        }).catch((error) => {
            console.log(`Content order change error day 1 ${error}`);
            
        })

        // Day 2
        await container.get<IHttpRequest>(TYPES.IHttpRequest).get(`${baseurl}?${urlBuilder(Toggle.date2)}&top=100${orderByBuilder(orderBy)}`).then((x) => {

            day2Data = x.data.data;

        }).catch((error) => {
            console.log(`Content order change error day 2 ${error}`);
            
        })

        return {day1Data, day2Data}
    }

    function setContentPopOnSearch(day1Data, contentPopDay2) {

        if (!day1Data || !contentPopDay2){
            return
        }

        const contentPopDay1 = day1Data.insightsContentPopularityResource.data;

        const rankings = addRankings(contentPopDay1, contentPopDay2);

        // Set Aggregated data once rankings computed
        day1Data.insightsContentPopularityResource.data = rankings;
        setAggregatedDayOne(day1Data);

        // Because content popularity ranking depends on both days data only load at end
        setContentPop({
            title:'Content Pop',
            tooltip:'',
            data:rankings,
            columns:columnsContentPopRankings(),
            csvDownload: `${apiSettings.apiBaseUrl()}insights/content-popularity-csv?${urlBuilder(Toggle.date1)}`
        });
    }

    function setDayTwoModal(day2Data:any){

        // Slight hack - needs directionStats to show modal not link on toggle day 2
        return day2Data.map((item,idx)=> ({ ...item, id: {directionStats:  {  leaderBoardPosition:idx+1, itemId:item.id} } }));
    }

    function urlBuilder(day:Toggle):string {

        const {date1, date2, selectedUserGroups, selectedWorkspace, existingItem} = searchFilterOptions;

        let url = ""
        
        // Dates
        if (day === Toggle.date1){
            url += `dateFrom=${removeTimeZone(date1.dateFrom)}&dateTo=${removeTimeZone(date1.dateTo)}` 
        }

        if (day === Toggle.date2){
            url += `dateFrom=${removeTimeZone(date2.dateFrom)}&dateTo=${removeTimeZone(date2.dateTo)}`
        }

        // Workspaces
        if (selectedWorkspace && selectedWorkspace.value){
            url+= `&workspaceGuids=${selectedWorkspace.value}`
        }

        // Groups
        if (selectedUserGroups !== null){    
            selectedUserGroups.forEach((g) => {
                url+= `&userGroupIds=${g.value}`;
            });
        }

        // Content item
        if (existingItem && existingItem.length){
            
            existingItem.forEach((item, idx) => {
                url += `&contentId[${idx}]=${item.id}`
            })
        }

        return url
    }

    function orderByBuilder(order:OrderBy):string {

        if (order !== null){
            return `&orderBy=${order.column}&direction=${order.direction}`;
        }

        return ""
    } 

    function moveDataOnSecondToggle(day2Data:any):void {

        if (contentPopToggle === Toggle.date2){
            contentPopToggleDataSet(day2Data.insightsContentPopularityResource.data);
        }

        if (userGroupToggle === Toggle.date2){
            userGroupToggleDataSet(day2Data.insightsUserGroupActivityResource.data);
        }

        if (twoColToggle === Toggle.date2){

            listPopToggleDataSet(day2Data.insightsListPopularityResource.data);

            chanPopDataSet(day2Data.insightsChannelPopularityResource.data);
        }
    }

    function contentPopToggleDataSet(data:any):void {

        setContentPop(prevState => ({
            ...prevState,
            data: data,
            csvDownload: `${apiSettings.apiBaseUrl()}insights/content-popularity-csv?${urlBuilder(contentPopToggle)}`
        }));
    }

    function userGroupToggleDataSet(data:any):void {

        setUserGroup(prevState => ({
            ...prevState,
            data: data,
            csvDownload: `${apiSettings.apiBaseUrl()}insights/user-group-activity-csv?${urlBuilder(userGroupToggle)}`
        }));
    }

    function listPopToggleDataSet(data:any):void {

        setListPop(prevState => ({
            ...prevState,
            data: data,
            csvDownload: `${apiSettings.apiBaseUrl()}insights/list-popularity-csv?${urlBuilder(twoColToggle)}`
        }));
    }

    function chanPopDataSet(data:any):void {
        
        setChanPop(prevState => ({
            ...prevState,
            data: data,
            csvDownload: `${apiSettings.apiBaseUrl()}insights/channel-popularity-csv?${urlBuilder(twoColToggle)}`
       }));
    }

    function downloadLink():string {   
        return `${apiSettings.apiBaseUrl()}insights/workspace-insights-comparison-csv?${buildCompareDownloadLink(searchFilterOptions)}`;
    }
  
    // Gen error
    if (genError){
        return (
            <GenError />
        )
    }
    
    return (
        <>     
            <SearchFilter 
                page={FilterPage.content} 
                setSearchFilterOptions={setSearchFilterOptions}
            />

            {!initLoad && 
                <>

                    <div className="breakdown-container">
                        <BreakdownCompare dayOne={breakdownDayOne} dayTwo={breakdownDayTwo} downloadLink={downloadLink()} />
                    </div>

                    <div className="toggle-container">
                        
                        <h2 className="table-header">Content Popularity</h2>

                        <ToolTipWrapper id="content-pop-2" className="content-pop-2" >                     
                            <ReactTooltip place="top" className='thrive-tooltip' delayHide={1000}  id="content-pop-2" >
                                <span>{tooltipData.contentPop}</span>
                            </ReactTooltip>
                        </ToolTipWrapper>

                        <section className="toggle">
                            <span className="title">TOGGLE DATE</span>
                            <div onClick={() => setContentPopToggle(Toggle.date1)} className={contentPopToggle === Toggle.date1 ? 'selected':''}><DateLabelBuilder toggle={Toggle.date1} searchFilterOptions={searchFilterOptions} /></div>
                            <div onClick={() => setContentPopToggle(Toggle.date2)} className={contentPopToggle === Toggle.date2 ? 'selected':''}><DateLabelBuilder toggle={Toggle.date2} searchFilterOptions={searchFilterOptions}  /></div>
                        </section>

                        <ContentTable content={contentPop} className="content-table-8" setOrder={setContentPopOrderBy} defaultOrder={contentPopOrderBy} detailsModal={true} />

                    </div>
                    
                    <div className="toggle-container">
                        
                        <h2 className="table-header">User Group Activity</h2>

                        <ToolTipWrapper id="user-group-2" className="user-group-2" >                     
                            <ReactTooltip place="top" className='thrive-tooltip' delayHide={1000}  id="user-group-2" >
                                <span>{tooltipData.contentPop}</span>
                            </ReactTooltip>
                        </ToolTipWrapper>
        
                        <section className="toggle">
                            <span className="title">TOGGLE DATE</span>
                            <div onClick={() => setUserGroupToggle(Toggle.date1)} className={userGroupToggle === Toggle.date1 ? 'selected':''}><DateLabelBuilder toggle={Toggle.date1} searchFilterOptions={searchFilterOptions} /></div>
                            <div onClick={() => setUserGroupToggle(Toggle.date2)} className={userGroupToggle === Toggle.date2 ? 'selected':''}><DateLabelBuilder toggle={Toggle.date2} searchFilterOptions={searchFilterOptions} /></div>
                        </section>
                        
                        <ContentTable content={userGroup} className="content-table-9" setOrder={setUserGroupOrderBy} defaultOrder={userGroupOrderBy} />
                        
                    </div>

                    <div className="toggle-container">
                        
                        <section className="toggle">
                            <span className="title">TOGGLE DATE</span>
                            <div onClick={() => setTwoColToggle(Toggle.date1)} className={twoColToggle === Toggle.date1 ? 'selected':''}><DateLabelBuilder toggle={Toggle.date1} searchFilterOptions={searchFilterOptions}  /></div>
                            <div onClick={() => setTwoColToggle(Toggle.date2)} className={twoColToggle === Toggle.date2 ? 'selected':''}><DateLabelBuilder toggle={Toggle.date2} searchFilterOptions={searchFilterOptions}  /></div>
                        </section>
                    
                        <div className="two-col">
                            
                            <ContentTable content={listPop} className="content-table-5" setOrder={setListPopOrderBy} defaultOrder={listPopOrderBy} />

                            <ContentTable content={chanPop} className="content-table-5" setOrder={setChanPopOrderBy} defaultOrder={chanPopOrderBy} />
                                    
                        </div>

                    </div> 
                        
                </>
            }
        </>
    );
};

export default Content;